import { useCallback, useEffect, useState } from "react";

import { createReference, parseReference, WithId } from "@medplum/core";
import { Encounter, Observation } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { LOINCCodes } from "@/fhir/enums/loinc";
import {
  getCodeableConceptCode,
  getResourcesFromBundle,
  joinReferenceString,
} from "@/utils/medplum";

/** Flowsheet observation, member observations, and that encounter's period.start for GA (if known). */
export type PrenatalFlowsheetObservationRow = [
  WithId<Observation>,
  Observation[],
  string | undefined,
];

// TODO: filter by current pregnancy of patient, not patient:

export function usePrenatalFlowsheet({
  encounterId,
  clientId,
}: {
  encounterId: string;
  clientId: string;
}) {
  const medplum = useMedplum();
  const [observations, setObservations] = useState<PrenatalFlowsheetObservationRow[] | undefined>(
    undefined,
  );
  const [encounter, setEncounter] = useState<Encounter>();
  const [isLoading, setIsLoading] = useState(true);
  const [refreshData, setRefresh] = useState(false); // changes to trigger a refresh
  const refresh = () => setRefresh(!refreshData);

  const prenatalFlowsheetObservations = useCallback(async () => {
    const encounter = await medplum.searchOne("Encounter", {
      _id: encounterId,
    });
    if (!encounter) return;

    setEncounter(encounter);
    const startDate = encounter.period?.start;

    let encountersLinkedWithTheSameEpisodeOfCareIds: string[] = [];
    const encounterRefToPeriodStart = new Map<string, string | undefined>();

    const episodeOfCare = encounter?.episodeOfCare;
    const clientReference = joinReferenceString("Patient", clientId);
    if (episodeOfCare?.length) {
      // Collects Encounters within the same Episode of Care
      // and that were started before the current encounter's start date
      const encountersLinkedWithTheSameEpisodeOfCare = await medplum.searchResources("Encounter", {
        "episode-of-care": episodeOfCare[0].reference,
        subject: clientReference,
        "_id:not": encounterId,
        _count: 1000,
        // Only filter by date when the current Encounter has a start date. Without this check the
        // filter becomes "ltundefined", which fails the search. The table then shows an empty
        // flowsheet, so the failure looks like the client simply has no history.
        ...(startDate ? { date: `lt${startDate}` } : {}),
      });

      const prenatalEncountersInEpisode =
        getResourcesFromBundle(
          "Encounter",
          encountersLinkedWithTheSameEpisodeOfCare?.bundle,
        )?.filter(
          (encounter) =>
            getCodeableConceptCode(encounter.serviceType) === LOINCCodes.PrenatalEncounterType,
        ) ?? [];

      for (const encounterInEpisode of prenatalEncountersInEpisode) {
        encounterRefToPeriodStart.set(
          createReference(encounterInEpisode).reference,
          encounterInEpisode.period?.start,
        );
      }

      encountersLinkedWithTheSameEpisodeOfCareIds = prenatalEncountersInEpisode.map(
        (encounterInEpisode) => createReference(encounterInEpisode).reference,
      );
    }

    // Downloads all Prenatal FlowSheet Observations from all related Encounters found
    const flowsheetObservationsRequests = encountersLinkedWithTheSameEpisodeOfCareIds.map(
      (encounter) =>
        medplum.searchOne("Observation", {
          encounter,
          subject: clientReference,
          code: LOINCCodes.PrenatalFlowSheet,
          _sort: "-_lastUpdated", // Ensures we get only the latest updated Obsevation for each Encounter
        }),
    );
    const flowsheetObservations = (await Promise.all(flowsheetObservationsRequests)).filter(
      Boolean,
    ) as WithId<Observation>[];

    if (!flowsheetObservations.length) return setObservations(undefined);

    // Downloads all Observations in the "hasMember" field of each Flowsheet Observation
    const membersRequests = flowsheetObservations
      .map((observation) => {
        const { hasMember } = observation;
        if (!hasMember) return;
        return medplum.searchResources("Observation", {
          _id: hasMember?.map((member) => parseReference(member)[1]).join(","),
          subject: clientReference,
        });
      })
      .filter(Boolean);

    // Maps each Observation from "hasMember" by their reference, so they can be quickly accessed later
    const membersByReference = (await Promise.all(membersRequests))
      .filter(Boolean)
      .flatMap((response) => getResourcesFromBundle("Observation", response!.bundle))
      .reduce(
        (result, memberObservation) => ({
          ...result,
          [createReference(memberObservation).reference]: memberObservation,
        }),
        {},
      ) as Record<string, Observation>;

    setObservations(
      flowsheetObservations
        .sort((a, b) => {
          const aDate = a.effectiveDateTime ?? a.meta?.lastUpdated;
          const bDate = b.effectiveDateTime ?? b.meta?.lastUpdated;
          const aTime = aDate ? new Date(aDate).getTime() : 0;
          const bTime = bDate ? new Date(bDate).getTime() : 0;
          return bTime - aTime;
        }) // Sorts the Flowsheet Observations by theirs dates in DESC order;
        .map((observation) => {
          const encounterRef = observation.encounter?.reference;
          const encounterPeriodStart = encounterRef
            ? encounterRefToPeriodStart.get(encounterRef)
            : undefined;
          return [
            observation,
            observation.hasMember?.map(
              (member) => membersByReference[member.reference!],
            ) as Observation[],
            encounterPeriodStart,
          ];
        }),
    );
  }, [clientId, encounterId, medplum]);

  useEffect(() => {
    setIsLoading(true);
    prenatalFlowsheetObservations().finally(() => {
      setIsLoading(false);
    });
  }, [prenatalFlowsheetObservations, refreshData]);

  return { encounter, observations, isLoading, refresh };
}
